import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft, Plus, Save, Trash2, ChevronUp, ChevronDown,
  Link as LinkIcon, Eye, EyeOff, X, Loader2, Image as ImageIcon, ExternalLink, FileText, Upload, Info
} from "lucide-react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Banner } from "../../../components/common/Banner";
import TooltipHelper from "../../../components/common/TooltipHelper";
import SectionTips from "../../../components/common/SectionTips";
import {
  getMaterialsContent,
  updateMaterialsContent,
  uploadMaterialImage,
  deleteMaterialImageByUrl,
  uploadMaterialFile,
  getMaterialCategories,
  createMaterialCategory,
  updateMaterialCategory,
  deleteMaterialCategory,
  uploadMaterialCategoryImage,
  deleteMaterialCategoryImageByUrl,
} from "../../../services/materialContentService";

/* ---------- estilos / util (alineado a tus otras páginas) ---------- */
const input = "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const textareaBase = "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const btn = "inline-flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap";
const btnPri = `${btn} bg-[#0488AF] text-white hover:bg-[#037a98] disabled:opacity-60 disabled:cursor-not-allowed`;
const btnGhost = `${btn} border bg-white hover:bg-gray-50 disabled:opacity-60`;
const btnIcon = "px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40";
const btnDanger = "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50";
const btnAddBlue = "inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B4A8F] text-white hover:bg-[#093a70] shadow-sm";
const card = "rounded-2xl border bg-white ring-1 ring-black/5 overflow-hidden";

const Divider = () => (
  <div className="my-4">
    <div className="h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent rounded-full" />
  </div>
);


/* === Reglas de URL iguales al backend === */
const isValidUrl = (s) => {
  if (!s) return true;
  const v = String(s).trim();
  if (v.startsWith("/")) return true;
  if (v.toLowerCase().startsWith("mailto:")) return v.slice(7).includes("@");
  try { const u = new URL(v); return u.protocol === "http:" || u.protocol === "https:"; }
  catch { return false; }
};

// id local estable para keys
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2,8)}`;
const MAX_IMAGE_MB = 6;
const MAX_FILE_MB = 50;

// Obtener nombre del archivo desde URL
const getFileName = (url) => {
  if (!url) return "Archivo";
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "Archivo";
  } catch {
    return url.split("/").pop() || "Archivo";
  }
};

// Obtener imagen de categoría (se usará con categorías cargadas desde backend)
const getCategoryImage = (category, categoriesList) => {
  if (!category || !categoriesList) return null;
  const cat = categoriesList.find(c => c.key === category);
  return cat?.imageUrl || null;
};

// Generar key automáticamente desde el label (slug)
const generateKeyFromLabel = (label) => {
  if (!label) return "";
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Eliminar acentos
    .replace(/[^a-z0-9\s-]/g, "") // Eliminar caracteres especiales
    .trim()
    .replace(/\s+/g, "-") // Reemplazar espacios con guiones
    .replace(/-+/g, "-") // Eliminar guiones múltiples
    .replace(/^-|-$/g, ""); // Eliminar guiones al inicio/final
};

export default function MaterialReportsCards({ onBack }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    if (window.history.length > 1) return navigate(-1);
    navigate("/profile");
  };

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]); // [{_id,title,description,url,imageUrl,imagePublicId,createdAt,updatedAt}]
  const [original, setOriginal] = useState([]);
  const [collapsed, setCollapsed] = useState(new Set());
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [uploadingFileIdx, setUploadingFileIdx] = useState(null);
  const [categories, setCategories] = useState([]); // Categorías cargadas desde backend
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryImageUploading, setCategoryImageUploading] = useState(false);
  const hiddenFileRef = useRef(null);
  const hiddenFileUploadRef = useRef(null);
  const hiddenCategoryImageRef = useRef(null);

  const toggleCollapsed = (i) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const normalize = (arr) =>
    (Array.isArray(arr) ? arr : []).map(s => ({
      _id: s?._id || uid(),
      title: String(s?.title || "").trim(),
      description: s?.description ? String(s.description).trim() : "",
      url: s?.url ? String(s.url).trim() : "",
      imageUrl: s?.imageUrl ? String(s.imageUrl).trim() : "",
      imagePublicId: s?.imagePublicId ? String(s.imagePublicId).trim() : "",
      category: s?.category ? String(s.category).trim() : "",
      fileUrl: s?.fileUrl ? String(s.fileUrl).trim() : "",
      filePublicId: s?.filePublicId ? String(s.filePublicId).trim() : "",
      createdAt: s?.createdAt || undefined,
      updatedAt: s?.updatedAt || undefined,
    }));

  async function loadCategories() {
    try {
      const cats = await getMaterialCategories().catch(() => []);
      setCategories(cats || []);
    } catch (e) {
      console.error("Error cargando categorías:", e);
    }
  }

  async function load() {
    try {
      setLoading(true);
      const c = await getMaterialsContent().catch(() => ({ sections: [] }));
      const list = normalize(c?.sections);
      setSections(list);
      setOriginal(list);
      // Inicializar todas las secciones como contraídas por defecto
      setCollapsed(new Set(list.map((_, i) => i)));
    } catch (e) {
      const msg = Array.isArray(e?.data?.message) ? e.data.message.join(", ") : (e?.message || "No se pudo cargar el contenido");
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { 
    load();
    loadCategories();
  }, []);

  const addSection = () => {
    setSections((arr) => {
      const newArr = [
        { _id: uid(), title: "", description: "", url: "", imageUrl: "", category: "", fileUrl: "" },
        ...arr,
      ];
      // Actualizar el estado de colapsadas: nueva sección expandida (índice 0), demás contraídas
      setCollapsed(new Set(newArr.map((_, i) => i).filter(i => i > 0)));
      return newArr;
    });
  };

  const updateSection = (i, patch) =>
    setSections((arr) => {
      const next = [...arr];
      next[i] = { ...(next[i] || {}), ...patch };
      return next;
    });

  const moveSection = (i, dir) =>
    setSections((arr) => {
      const next = [...arr];
      const ni = i + dir;
      if (ni < 0 || ni >= next.length) return arr;
      [next[i], next[ni]] = [next[ni], next[i]];
      return next;
    });

  const removeSection = async (i) => {
    const section = sections[i];
    const r = await Swal.fire({
      icon: "question",
      title: "Eliminar sección",
      text: `¿Seguro que deseas eliminar "${
        section?.title || "esta sección"
      }"? Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    setSections((arr) => arr.filter((_, idx) => idx !== i));
  };

  const payloadClean = () =>
    (sections || [])
      .map(({ title, description, url, imageUrl, imagePublicId, category, fileUrl, filePublicId, createdAt, updatedAt }) => ({
        title: String(title || "").trim() || "Material",
        description: String(description || "").trim() || undefined,
        url: String(url || "").trim() || undefined,
        imageUrl: String(imageUrl || "").trim() || undefined,
        imagePublicId: String(imagePublicId || "").trim() || undefined,
        category: String(category || "").trim() || undefined,
        fileUrl: String(fileUrl || "").trim() || undefined,
        filePublicId: String(filePublicId || "").trim() || undefined,
        createdAt: createdAt || undefined,
        updatedAt: updatedAt || undefined,
      }))
      .filter((s) => s.title || s.description || s.url || s.imageUrl || s.fileUrl);

  const isDirty = useMemo(() => {
    const A = JSON.stringify(payloadClean());
    const B = JSON.stringify(
      (original || []).map(({ title, description, url, imageUrl, imagePublicId, category, fileUrl, filePublicId, createdAt, updatedAt }) => ({
        title,
        description: description || undefined,
        url: url || undefined,
        imageUrl: imageUrl || undefined,
        imagePublicId: imagePublicId || undefined,
        category: category || undefined,
        fileUrl: fileUrl || undefined,
        filePublicId: filePublicId || undefined,
        createdAt: createdAt || undefined,
        updatedAt: updatedAt || undefined,
      }))
    );
    return A !== B;
  }, [sections, original]);

  const isSectionDirty = useMemo(() => {
    return (i) => {
      if (i < 0 || i >= sections.length || i >= original.length) return false;
      const current = sections[i];
      const orig = original.find(o => o._id === current?._id) || original[i];
      if (!current || !orig) return false;
      
      const cleanCurrent = {
        title: String(current.title || "").trim() || "Material",
        description: String(current.description || "").trim() || undefined,
        url: String(current.url || "").trim() || undefined,
        imageUrl: String(current.imageUrl || "").trim() || undefined,
        imagePublicId: String(current.imagePublicId || "").trim() || undefined,
        category: String(current.category || "").trim() || undefined,
        fileUrl: String(current.fileUrl || "").trim() || undefined,
        filePublicId: String(current.filePublicId || "").trim() || undefined,
      };
      
      const cleanOrig = {
        title: orig.title || "Material",
        description: orig.description || undefined,
        url: orig.url || undefined,
        imageUrl: orig.imageUrl || undefined,
        imagePublicId: orig.imagePublicId || undefined,
        category: orig.category || undefined,
        fileUrl: orig.fileUrl || undefined,
        filePublicId: orig.filePublicId || undefined,
      };
      
      return JSON.stringify(cleanCurrent) !== JSON.stringify(cleanOrig);
    };
  }, [sections, original]);

  const saveSection = async (i) => {
    if (i < 0 || i >= sections.length) return;
    const section = sections[i];
    
    if (section.url && !isValidUrl(section.url)) {
      return Swal.fire(
        "URL inválida",
        `La URL debe iniciar con http(s)://, / o mailto:`,
        "warning"
      );
    }

    try {
      setSaving(true);
      const sectionsClean = payloadClean();
      await updateMaterialsContent({ sections: sectionsClean });
      await Swal.fire({ icon: "success", title: "Material guardado", timer: 900, showConfirmButton: false });
      await load();
    } catch (e) {
      const msg = Array.isArray(e?.data?.message) ? e.data.message.join(", ") : (e?.message || "No se pudo guardar");
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const discardSection = (i) => {
    if (i < 0 || i >= sections.length || i >= original.length) return;
    const orig = original.find(o => o._id === sections[i]?._id) || original[i];
    if (!orig) return;
    
    setSections((arr) => {
      const next = [...arr];
      next[i] = { ...orig };
      return next;
    });
  };

  const save = async () => {
    const sectionsClean = payloadClean();

    for (const s of sectionsClean) {
      if (s.url && !isValidUrl(s.url)) {
        return Swal.fire(
          "URL inválida",
          `En "${s.title}", la URL debe iniciar con http(s)://, / o mailto:`,
          "warning"
        );
      }
    }

    try {
      setSaving(true);
      await updateMaterialsContent({ sections: sectionsClean });
      await Swal.fire({ icon: "success", title: "Contenido guardado", timer: 900, showConfirmButton: false });
      await load();
    } catch (e) {
      const msg = Array.isArray(e?.data?.message) ? e.data.message.join(", ") : (e?.message || "No se pudo guardar");
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  /* ========== Imagen por sección (Cloudinary) ========== */
  const onClickPickImage = (idx) => {
    setUploadingIdx(idx);
    if (hiddenFileRef.current) hiddenFileRef.current.value = null;
    hiddenFileRef.current?.click();
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploadingIdx == null) return;
    if (!/^image\//.test(file.type)) {
      Swal.fire("Archivo inválido", "Selecciona una imagen (JPG/PNG).", "warning");
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      const mb = (file.size / 1024 / 1024).toFixed(2);
      Swal.fire("Imagen muy grande", `Máximo ${MAX_IMAGE_MB}MB (tiene ${mb}MB).`, "warning");
      return;
    }

    try {
      updateSection(uploadingIdx, { __imgUploading: true });
      const resp = await uploadMaterialImage(file); // {secureUrl, publicId}
      updateSection(uploadingIdx, {
        imageUrl: resp?.secureUrl || resp?.url || "",
        imagePublicId: resp?.publicId || "",
        __imgUploading: false,
      });
      Swal.fire({ icon: "success", title: "Imagen subida", timer: 900, showConfirmButton: false });
    } catch (err) {
      updateSection(uploadingIdx, { __imgUploading: false });
      Swal.fire("Error", err?.message || "No se pudo subir la imagen", "error");
    } finally {
      setUploadingIdx(null);
    }
  };

  const removeImage = async (idx) => {
    const url = sections[idx]?.imageUrl;
    if (!url) return updateSection(idx, { imageUrl: "", imagePublicId: "" });

    const r = await Swal.fire({
      icon: "question",
      title: "Quitar imagen",
      text: "Se eliminará la imagen de Cloudinary. ¿Continuar?",
      showCancelButton: true,
      confirmButtonText: "Sí, quitar",
      cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;

    try {
      updateSection(idx, { imageUrl: "", imagePublicId: "" });
      await deleteMaterialImageByUrl(url).catch(() => {});
      Swal.fire({ icon: "success", title: "Imagen eliminada", timer: 800, showConfirmButton: false });
    } catch {
      /* opcional: recargar desde backend */
    }
  };

  /* ========== Archivo por sección ========== */
  const onFileUploadPicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploadingFileIdx == null) return;
    
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const allowedExts = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];
    const fileExt = '.' + (file.name.split('.').pop() || '').toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
      Swal.fire("Archivo inválido", "Selecciona un archivo PDF, DOC, DOCX, XLS o XLSX.", "warning");
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      const mb = (file.size / 1024 / 1024).toFixed(2);
      // Si es PDF, mostrar mensaje con link de compresión
      const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
      if (isPdf) {
        Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          html: `El archivo "${file.name}" es demasiado grande (máx ${MAX_FILE_MB} MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
          confirmButtonText: "Entendido",
        });
        return;
      }
      Swal.fire("Archivo muy grande", `Máximo ${MAX_FILE_MB}MB (tiene ${mb}MB).`, "warning");
      return;
    }

    try {
      updateSection(uploadingFileIdx, { __fileUploading: true });
      const resp = await uploadMaterialFile(file); // {secureUrl, publicId}
      updateSection(uploadingFileIdx, {
        fileUrl: resp?.secureUrl || resp?.url || "",
        filePublicId: resp?.publicId || "",
        __fileUploading: false,
      });
      Swal.fire({ icon: "success", title: "Archivo subido", timer: 900, showConfirmButton: false });
    } catch (err) {
      updateSection(uploadingFileIdx, { __fileUploading: false });
      Swal.fire("Error", err?.message || "No se pudo subir el archivo", "error");
    } finally {
      setUploadingFileIdx(null);
    }
  };

  /* ========== expandir/contraer todo ========== */
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(sections.map((_, i) => i)));

  /* ========================= RENDER ========================= */
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="p-6 max-w-7xl mx-auto space-y-6"
    >
      <button
        onClick={goBack}
        className="inline-flex items-center gap-3 px-3 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-xl text-white"
      >
        <ArrowLeft size={18} /> Volver al menú principal
      </button>

      {/* Banner institucional (igual que otros módulos) */}
      <Banner
        title="Material de Interés"
        message="Gestiona tarjetas con título, descripción, vínculo y una imagen opcional."
      />

      <SectionTips
        section="la sección de Material de Interés"
        buttonTitle="¿Cómo usar esta sección?"
        tipsTitle="¿Cómo usar Material de Interés?"
        tipsList={[
          'En esta sección puedes crear y gestionar tarjetas de material de interés que se mostrarán a los socios.',
          'Cada tarjeta puede tener título, descripción, vínculo (URL), categoría e imagen opcional.',
          'Puedes organizar las tarjetas por categorías para facilitar la navegación de los socios.',
          'Puedes reordenar las tarjetas usando los botones ↑/↓.',
          'También puedes agregar archivos adjuntos (PDF, DOC, etc.) a cada tarjeta.',
          'Las tarjetas se muestran en la página pública para los socios con acceso a la documentación oficial.',
        ]}
        finalNote="Consejo: Organiza bien las categorías y las tarjetas para crear una experiencia clara y útil para los socios."
        color="#0488AF"
        videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766416064/Video_Tutorial_m%C3%B3dulo_Administraci%C3%B3n_-_Material_de_Inter%C3%A9s_qgptz2.mp4"
        manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412946/Manual_de_usuario_administraci%C3%B3n_bsvsyl.pdf"
      />

      {/* Header de acciones con gradiente */}
      <div className="rounded-2xl p-[1.5px]" style={{ background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)" }}>
        <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
          <div className="p-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Contenido público</h2>
            <div className="flex items-center gap-2">
              <button 
                type="button" 
                className={btnGhost} 
                onClick={() => setShowCategoriesModal(true)} 
                disabled={loading || saving}
                title="Gestionar categorías"
              >
                <ImageIcon size={16}/> <span className="hidden sm:inline">Gestionar categorías</span>
              </button>
              <button type="button" className={btnGhost} onClick={expandAll} disabled={loading || saving}>
                <Eye size={16}/> <span className="hidden sm:inline">Expandir todo</span>
              </button>
              <button type="button" className={btnGhost} onClick={collapseAll} disabled={loading || saving}>
                <EyeOff size={16}/> <span className="hidden sm:inline">Contraer todo</span>
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || loading || !isDirty}
                className={btnPri}
                title={!isDirty ? "Sin cambios" : "Guardar cambios"}
              >
                {saving ? <><Loader2 className="animate-spin" size={16} /> Guardando…</> : <><Save size={16} /> Guardar</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Botón añadir material - arriba */}
      <div className="flex justify-start">
        <button type="button" onClick={addSection} className={btnAddBlue} disabled={loading || saving}>
          <Plus size={16} /> Añadir material
        </button>
      </div>

      {/* Lista editable (tarjetas) */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-gray-500 py-6">Cargando…</div>
        ) : (sections.length ? sections : []).map((s, i) => {
            const isCollapsed = collapsed.has(i);
            const uploadingThis = s.__imgUploading === true;
            return (
              <div key={s._id} className={card}>
                <div className="h-1 w-full bg-gradient-to-r from-[#0488AF] to-[#C42251]" />
                <div className="p-4">
                  {/* Barra de acciones por sección */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm text-gray-700 mb-1 font-medium">Título</label>
                      <input
                        className={`${input} font-medium`}
                        value={s.title || ""}
                        onChange={(e) => updateSection(i, { title: e.target.value })}
                        placeholder={!s.title ? "Nuevo material" : "Título del material"}
                        disabled={saving}
                      />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button className={btnIcon} onClick={() => toggleCollapsed(i)} disabled={saving} title={isCollapsed ? "Expandir" : "Contraer"}>
                        {isCollapsed ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                      <button className={btnIcon} onClick={() => moveSection(i, -1)} disabled={i === 0 || saving} title="Subir">
                        <ChevronUp size={16} />
                      </button>
                      <button className={btnIcon} onClick={() => moveSection(i, +1)} disabled={i === sections.length - 1 || saving} title="Bajar">
                        <ChevronDown size={16} />
                      </button>
                      <button className={btnDanger} onClick={() => removeSection(i)} disabled={saving} title="Eliminar sección">
                        <Trash2 size={16} /> <span className="hidden sm:inline">Eliminar</span>
                      </button>
                    </div>
                  </div>

                  {/* Cuerpo expandible */}
                  {!isCollapsed && (
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      {/* Descripción */}
                      <label className="md:col-span-2">
                        <span className="block text-sm text-gray-700 mb-1">Descripción</span>
                        <textarea
                          className={`${textareaBase} min-h-[92px]`}
                          value={s.description || ""}
                          onChange={(e) => updateSection(i, { description: e.target.value })}
                          placeholder="Breve resumen…"
                          disabled={saving}
                        />
                      </label>

                      {/* URL */}
                      <label className="md:col-span-2">
                        <span className="block text-sm text-gray-700 mb-1">Vínculo a la fuente (http/https, /ruta o mailto:)</span>
                        <div className="flex items-center gap-2">
                          <LinkIcon size={16} className="text-gray-500" />
                          <input
                            className={input}
                            value={s.url || ""}
                            onChange={(e) => updateSection(i, { url: e.target.value })}
                            placeholder="https://sitio.com/articulo"
                            disabled={saving}
                          />
                          {!!s.url && isValidUrl(s.url) && (
                            <a
                              className="inline-flex items-center gap-1 text-[#0488AF] hover:underline"
                              href={s.url.startsWith("/") || s.url.toLowerCase().startsWith("mailto:")
                                ? s.url
                                : (isValidUrl(s.url) ? s.url : "#")}
                              target={s.url.startsWith("/") || s.url.toLowerCase().startsWith("mailto:") ? "_self" : "_blank"}
                              rel="noreferrer"
                              title="Abrir enlace"
                            >
                              <ExternalLink size={16}/>
                            </a>
                          )}
                        </div>
                        {s.url && !isValidUrl(s.url) && (
                          <div className="text-xs text-rose-700 mt-1">
                            Debe comenzar con http(s)://, / o mailto:
                          </div>
                        )}
                      </label>

                      {/* Categoría */}
                      <label className="md:col-span-2">
                        <span className="block text-sm text-gray-700 mb-1 flex items-center gap-1">
                          Categoría
                          <TooltipHelper text="Puedes usar la imagen de la categoría o subir una imagen personalizada. También permite filtrar los materiales por categoría en la página pública." />
                        </span>
                        <select
                          className={input}
                          value={s.category || ""}
                          onChange={(e) => {
                            const selectedCategory = categories.find(c => c.key === e.target.value);
                            // No cambiar la imagen automáticamente, solo actualizar la categoría
                            // El usuario decidirá si quiere usar la imagen de la categoría
                            updateSection(i, { 
                              category: e.target.value,
                            });
                          }}
                          disabled={saving}
                        >
                          <option value="">Sin categoría</option>
                          {categories.map(cat => (
                            <option key={cat.key} value={cat.key}>{cat.label}</option>
                          ))}
                        </select>
                      </label>

                      {/* Imagen de sección */}
                      <div className="md:col-span-2">
                        <span className="block text-sm text-gray-700 mb-2">Imagen (opcional)</span>

                        {(() => {
                          const categoryImage = s.category ? getCategoryImage(s.category, categories) : null;
                          const isUsingCategoryImage = categoryImage && s.imageUrl === categoryImage;
                          const hasPersonalImage = s.imageUrl && !isUsingCategoryImage;

                          return (
                            <div className="space-y-3">
                              {/* Vista previa de imagen */}
                              {(s.imageUrl || categoryImage) && (
                                <div className="flex flex-col sm:flex-row gap-3 items-start min-w-0">
                                  <div className="rounded-xl border bg-white p-2 flex-shrink-0">
                                    <img
                                      src={s.imageUrl || categoryImage}
                                      alt="Vista previa"
                                      className="h-28 w-auto object-cover rounded-lg"
                                      onError={(ev)=>{ ev.currentTarget.src=""; }}
                                    />
                                  </div>
                                  <div className="flex flex-wrap gap-2 min-w-0 flex-1">
                                    {s.imageUrl && (
                                      <a href={s.imageUrl} target="_blank" rel="noreferrer" className={btnGhost}>
                                        <Eye size={16}/> Ver imagen
                                      </a>
                                    )}
                                    {isUsingCategoryImage && (
                                      <span className="px-2 py-1 text-xs rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
                                        Usando imagen de categoría
                                      </span>
                                    )}
                                    {hasPersonalImage && (
                                      <span className="px-2 py-1 text-xs rounded-lg bg-green-50 text-green-700 border border-green-200">
                                        Imagen personalizada
                                      </span>
                                    )}
                                    {hasPersonalImage && (
                                      <button
                                        type="button"
                                        className={btnDanger}
                                        onClick={() => removeImage(i)}
                                        disabled={saving}
                                      >
                                        <Trash2 size={16}/> Quitar imagen personalizada
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Opciones de imagen */}
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className={btnGhost}
                                  onClick={() => onClickPickImage(i)}
                                  disabled={saving || uploadingThis}
                                  title="Subir imagen personalizada"
                                >
                                  {uploadingThis ? (
                                    <><Loader2 className="animate-spin" size={16}/> Subiendo…</>
                                  ) : (
                                    <><Upload size={16}/> {hasPersonalImage ? "Reemplazar imagen" : "Subir imagen personalizada"}</>
                                  )}
                                </button>
                              </div>

                              {categoryImage && !s.imageUrl && (
                                <div className="flex items-center">
                                  <TooltipHelper text="Esta categoría tiene una imagen por defecto. Puedes usarla o subir una imagen personalizada." />
                                </div>
                              )}
                              {!categoryImage && !s.imageUrl && (
                                <div className="text-xs text-gray-500">
                                  Formatos: JPG/PNG. Máx: {MAX_IMAGE_MB}MB.
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Archivo adjunto */}
                      <div className="md:col-span-2">
                        <span className="block text-sm text-gray-700 mb-2">Archivo adjunto (opcional)</span>
                        {s.fileUrl ? (
                          <div className="flex flex-col sm:flex-row gap-3 items-start min-w-0">
                            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-gray-50 flex-shrink-0">
                              <FileText size={18} className="text-[#0488AF]" />
                              <div className="flex flex-col min-w-0">
                                <span className="text-sm text-gray-700 font-medium">Archivo adjunto</span>
                                <span className="text-xs text-gray-600 truncate max-w-[200px]" title={getFileName(s.fileUrl)}>
                                  {getFileName(s.fileUrl)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 min-w-0 flex-1">
                              <a
                                href={s.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className={btnGhost}
                              >
                                <Eye size={16}/> Ver archivo
                              </a>
                              <button
                                type="button"
                                className={btnDanger}
                                onClick={() => updateSection(i, { fileUrl: "", filePublicId: "" })}
                                disabled={saving}
                              >
                                <Trash2 size={16}/> Quitar archivo
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              className={btnGhost}
                              onClick={() => {
                                setUploadingFileIdx(i);
                                if (hiddenFileUploadRef.current) hiddenFileUploadRef.current.value = null;
                                hiddenFileUploadRef.current?.click();
                              }}
                              disabled={saving || s.__fileUploading === true}
                              title="Subir archivo"
                            >
                              {s.__fileUploading === true ? (
                                <><Loader2 className="animate-spin" size={16}/> Subiendo…</>
                              ) : (
                                <><Upload size={16}/> Subir archivo</>
                              )}
                            </button>
                            <div className="text-xs text-gray-500">
                              Formatos: PDF, DOC, DOCX, XLS, XLSX. Máx: {MAX_FILE_MB}MB.
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Botones guardar y descartar por material */}
                      <div className="md:col-span-2 flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => discardSection(i)}
                          disabled={saving || loading || !isSectionDirty(i)}
                          className={btnGhost}
                          title="Descartar cambios de este material"
                        >
                          <X size={16} /> Descartar cambios
                        </button>
                        <button
                          type="button"
                          onClick={() => saveSection(i)}
                          disabled={saving || loading || !isSectionDirty(i)}
                          className={btnPri}
                          title="Guardar cambios de este material"
                        >
                          {saving ? <><Loader2 className="animate-spin" size={16} /> Guardando…</> : <><Save size={16} /> Guardar</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* Footer acciones globales */}
      <div className="flex flex-wrap gap-2 justify-end">
        <button type="button" onClick={save} disabled={saving || loading || !isDirty} className={btnPri}>
          <Save size={16} /> Guardar todo
        </button>
        <button
          type="button"
          onClick={() => load()}
          disabled={saving || loading}
          className={btnGhost}
          title="Recargar (descarta cambios locales)"
        >
          <X size={16} /> Descartar todo
        </button>
      </div>

      {/* input oculto para subir imagen */}
      <input
        ref={hiddenFileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFilePicked}
      />

      {/* input oculto para subir archivo */}
      <input
        ref={hiddenFileUploadRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={onFileUploadPicked}
      />

      <Divider />

      {/* Modal de gestión de categorías */}
      {showCategoriesModal && (
        <CategoriesModal
          categories={categories}
          editingCategory={editingCategory}
          setEditingCategory={setEditingCategory}
          categoryImageUploading={categoryImageUploading}
          setCategoryImageUploading={setCategoryImageUploading}
          hiddenCategoryImageRef={hiddenCategoryImageRef}
          onClose={() => {
            setShowCategoriesModal(false);
            setEditingCategory(null);
          }}
          onSave={async () => {
            await loadCategories();
            setEditingCategory(null);
          }}
        />
      )}
    </motion.div>
  );
}

// Componente modal para gestionar categorías
function CategoriesModal({ 
  categories, 
  editingCategory, 
  setEditingCategory, 
  categoryImageUploading, 
  setCategoryImageUploading,
  hiddenCategoryImageRef,
  onClose, 
  onSave 
}) {
  const [newCategory, setNewCategory] = useState({ key: "", label: "", imageUrl: null, imagePublicId: null });
  const [saving, setSaving] = useState(false);

  // Generar key automáticamente cuando cambia el label (solo para nuevas categorías)
  useEffect(() => {
    if (!editingCategory && newCategory.label) {
      const generatedKey = generateKeyFromLabel(newCategory.label);
      setNewCategory(prev => ({ ...prev, key: generatedKey }));
    }
  }, [newCategory.label, editingCategory]);

  const handleSaveCategory = async () => {
    if (!newCategory.label.trim()) {
      Swal.fire("Error", "El nombre es obligatorio", "warning");
      return;
    }

    // Generar key automáticamente si no existe o si es una nueva categoría
    const finalKey = editingCategory ? editingCategory.key : generateKeyFromLabel(newCategory.label);
    
    if (!finalKey) {
      Swal.fire("Error", "No se pudo generar una clave válida desde el nombre", "warning");
      return;
    }

    try {
      setSaving(true);
      if (editingCategory) {
        await updateMaterialCategory(editingCategory.key, {
          label: newCategory.label,
          imageUrl: newCategory.imageUrl,
          imagePublicId: newCategory.imagePublicId,
        });
        Swal.fire({ icon: "success", title: "Categoría actualizada", timer: 900, showConfirmButton: false });
      } else {
        await createMaterialCategory({
          key: finalKey,
          label: newCategory.label,
          imageUrl: newCategory.imageUrl,
          imagePublicId: newCategory.imagePublicId,
        });
        Swal.fire({ icon: "success", title: "Categoría creada", timer: 900, showConfirmButton: false });
      }
      setNewCategory({ key: "", label: "", imageUrl: null, imagePublicId: null });
      await onSave();
    } catch (e) {
      const msg = Array.isArray(e?.data?.message) ? e.data.message.join(", ") : (e?.message || "Error al guardar");
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (key) => {
    const r = await Swal.fire({
      icon: "question",
      title: "Eliminar categoría",
      text: "¿Seguro que deseas eliminar esta categoría?",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;

    try {
      await deleteMaterialCategory(key);
      Swal.fire({ icon: "success", title: "Categoría eliminada", timer: 900, showConfirmButton: false });
      await onSave();
    } catch (e) {
      const msg = Array.isArray(e?.data?.message) ? e.data.message.join(", ") : (e?.message || "Error al eliminar");
      Swal.fire("Error", msg, "error");
    }
  };

  const handleDeleteCategoryImage = async () => {
    if (!newCategory.imageUrl) return;

    const r = await Swal.fire({
      icon: "question",
      title: "Eliminar imagen",
      text: "¿Seguro que deseas eliminar esta imagen de Cloudinary?",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;

    try {
      setCategoryImageUploading(true);
      // Eliminar de Cloudinary
      await deleteMaterialCategoryImageByUrl(newCategory.imageUrl).catch(() => {});
      // Limpiar del estado
      setNewCategory({
        ...newCategory,
        imageUrl: null,
        imagePublicId: null,
      });
      Swal.fire({ icon: "success", title: "Imagen eliminada", timer: 900, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Error", err?.message || "No se pudo eliminar la imagen", "error");
    } finally {
      setCategoryImageUploading(false);
    }
  };

  const handleUploadCategoryImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      Swal.fire("Archivo inválido", "Selecciona una imagen (JPG/PNG).", "warning");
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      const mb = (file.size / 1024 / 1024).toFixed(2);
      Swal.fire("Imagen muy grande", `Máximo ${MAX_IMAGE_MB}MB (tiene ${mb}MB).`, "warning");
      return;
    }

    try {
      setCategoryImageUploading(true);
      
      // Si hay una imagen anterior, eliminarla de Cloudinary primero
      if (newCategory.imageUrl && newCategory.imagePublicId) {
        await deleteMaterialCategoryImageByUrl(newCategory.imageUrl).catch(() => {});
      }

      // Subir la nueva imagen
      const resp = await uploadMaterialCategoryImage(file);
      setNewCategory({
        ...newCategory,
        imageUrl: resp?.secureUrl || resp?.url || "",
        imagePublicId: resp?.publicId || "",
      });
      Swal.fire({ icon: "success", title: "Imagen subida", timer: 900, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Error", err?.message || "No se pudo subir la imagen", "error");
    } finally {
      setCategoryImageUploading(false);
      // Limpiar el input para permitir subir la misma imagen de nuevo
      if (hiddenCategoryImageRef.current) {
        hiddenCategoryImageRef.current.value = "";
      }
    }
  };

  const startEdit = (cat) => {
    setEditingCategory(cat);
    setNewCategory({
      key: cat.key,
      label: cat.label,
      imageUrl: cat.imageUrl || null,
      imagePublicId: cat.imagePublicId || null,
    });
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setNewCategory({ key: "", label: "", imageUrl: null, imagePublicId: null });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div 
        className="rounded-2xl p-[2px] max-w-2xl w-full max-h-[90vh] overflow-hidden"
        style={{ background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl ring-1 ring-black/5 flex flex-col max-h-[90vh] overflow-hidden">
          {/* Header con gradiente RDS */}
          <div
            className="px-5 py-4 text-white"
            style={{ background: "linear-gradient(90deg, #0c3b66, #0488AF)" }}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-base">Gestionar Categorías</div>
                <p className="text-[11px] text-white/90 mt-0.5">
                  {editingCategory ? "Editar categoría existente" : "Crear nueva categoría"}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Body scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Formulario crear/editar */}
            <div className={card}>
              <div className="p-4">
                <h4 className="font-medium mb-4 text-gray-800">{editingCategory ? "Editar categoría" : "Nueva categoría"}</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Nombre *</label>
                    <input
                      className={input}
                      value={newCategory.label}
                      onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
                      placeholder="ej: Inteligencia Artificial"
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Imagen</label>
                    <div className="flex items-center gap-3">
                      {newCategory.imageUrl ? (
                        <div className="relative">
                          <img src={newCategory.imageUrl} alt="Preview" className="h-20 w-20 object-cover rounded-lg border" />
                          <div className="absolute -top-2 -right-2 flex gap-1">
                            <button
                              type="button"
                              onClick={handleDeleteCategoryImage}
                              disabled={saving || categoryImageUploading}
                              className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600 disabled:opacity-50"
                              title="Eliminar imagen"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      ) : null}
                      <div className="flex gap-2">
                        {newCategory.imageUrl ? (
                          <button
                            type="button"
                            onClick={() => hiddenCategoryImageRef.current?.click()}
                            disabled={saving || categoryImageUploading}
                            className={`${btnGhost} ${categoryImageUploading ? "opacity-50" : ""}`}
                          >
                            {categoryImageUploading ? <><Loader2 className="animate-spin" size={16} /> Reemplazando…</> : <><Upload size={16} /> Reemplazar</>}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => hiddenCategoryImageRef.current?.click()}
                            disabled={saving || categoryImageUploading}
                            className={`${btnGhost} ${categoryImageUploading ? "opacity-50" : ""}`}
                          >
                            {categoryImageUploading ? <><Loader2 className="animate-spin" size={16} /> Subiendo…</> : <><Upload size={16} /> Subir imagen</>}
                          </button>
                        )}
                      </div>
                      <input
                        ref={hiddenCategoryImageRef}
                        type="file"
                        accept="image/*"
                        onChange={handleUploadCategoryImage}
                        className="hidden"
                      />
                    </div>
                    {newCategory.imageUrl && (
                      <div className="flex items-center mt-1">
                        <TooltipHelper text="Puedes eliminar la imagen o reemplazarla. Al reemplazar, la imagen anterior se eliminará automáticamente de Cloudinary." />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleSaveCategory} disabled={saving || categoryImageUploading} className={btnPri}>
                      {saving ? <><Loader2 className="animate-spin" size={16} /> Guardando…</> : <><Save size={16} /> {editingCategory ? "Actualizar" : "Crear"}</>}
                    </button>
                    {editingCategory && (
                      <button onClick={cancelEdit} disabled={saving} className={btnGhost}>
                        Cancelar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de categorías existentes */}
            <div>
              <h4 className="font-medium mb-3 text-gray-800">Categorías existentes</h4>
              <div className="space-y-2">
                {categories.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">No hay categorías creadas</p>
                ) : (
                  categories.map((cat) => (
                    <div key={cat.key} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                      {cat.imageUrl && (
                        <img src={cat.imageUrl} alt={cat.label} className="h-12 w-12 object-cover rounded border" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800">{cat.label}</div>
                      </div>
                      <button onClick={() => startEdit(cat)} className={btnIcon} disabled={saving} title="Editar categoría">
                        Editar
                      </button>
                      <button onClick={() => handleDeleteCategory(cat.key)} className={btnDanger} disabled={saving} title="Eliminar categoría">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
